import AddButton from "components/Button/AddButton";
import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DropdownStatusList, EmptyString, FormTypeAdd, PageSize, StatusCodeActive, StatusCodeDelete } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import Dropdown from "components/Input/Dropdown";
import { addSpaceEvery4, GetStatus, removeNonNumeric } from "utils/helpers";
import RevisionIcon from "assets/icon/RevisionIcon";
import FilterIcon from "assets/icon/FilterIcon";
import XIcon from "assets/icon/XIcon";
import { useModal } from "utils/context/ModalContext";
import Pagination from "components/Input/PaginationButton";
import { useCallApi } from "api/apiHooks";
import { FilterCard, GridDeleteButton, GridEditButton, GridViewButton, MainBodyCard, PagesTitle } from "components/Cards";
import DataTable from "components/DataTable";
import { useVariable } from "utils/context/VariableContext";

export default function Customer(){
    let navigate = useNavigate()

    const { isAdmin } = useVariable()

    const firstLoad = useRef(true)
    const debounceRef = useRef(null)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchCustomerList, fetchDeleteCustomer } = useCallApi()

    const [customerList, setCustomerList] = useState([])
    const [customerName, setCustomerName] = useState(EmptyString)
    const [address, setAddress] = useState(EmptyString)
    const [phone, setPhone] = useState(EmptyString)
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [isFilterOpen, setIsFilterOpen] = useState(false)

    // GRID
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    
    const isFilterOpenClick = () => {
        setIsFilterOpen(!isFilterOpen)
    }

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "customerName",
            header: "Nama Pelanggan"
        },
        {
            key: "phone",
            header: "Phone",
            render: (row) => addSpaceEvery4(row.phone),
            visible: isAdmin
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridViewButton onClick={() => viewBtnClick(row.customerId)} size="w-3" />
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.customerId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.customerId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]

    async function handlePageChange(newIndex) {
        try {
            showLoading()

            const res = await fetchCustomerList(customerName, address, phone, status, PageSize, parseInt(newIndex))

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setCustomerList(res)
            setTotalPages(total);
            setPageIndex(newIndex);
        } finally {
            hideLoading();
        }
    }
    
    const filterEnterKeyPress = (e) => {
		if(e.key === 'Enter'){
			handlePageChange(0)
		}
	}
    const addBtnClick = () => {
        navigate("/customer/form?customerId=&formType=" + FormTypeAdd)
    }
    const viewBtnClick = (customerId) => {
        navigate("/customer/view?customerId=" + customerId)
    }
    const editBtnClick = (customerId) => {
        navigate("/customer/form?customerId=" + customerId + "&formType=Edit")
    }
    const deleteBtnClick = async (customerId, status) => {
        const isConfirmed = await confirm()

        if (isConfirmed) {
            try {
                showLoading()

                const res = await fetchDeleteCustomer(customerId, status)
                if (!res) return
                
            } finally {
                hideLoading()

                showSuccess({
                    onSuccess: () => {
                        handlePageChange(pageIndex)
                    }
                })
            }
        }
    }

    useEffect(() => {
        const onLoad = async () => {
            try {
                if (firstLoad.current) {
                    handlePageChange(0)

                    firstLoad.current = false
                }
                else {
                    if (debounceRef.current) {
                        clearTimeout(debounceRef.current)
                    }
            
                    debounceRef.current = setTimeout(async () => {
                        await handlePageChange(0)
                    }, 500)
                }
            } finally {
                hideLoading()
            }
        }

        onLoad()
    },[customerName, phone, status])

    return(
        <MainBodyCard>
            <PagesTitle title='Pelanggan' />

            <div className="xl:hidden mt-5 flex justify-end">
                <button className="bg-black rounded-md flex gap-2 px-4 py-2" onClick={isFilterOpenClick}>
                    {isFilterOpen ? <XIcon className="fill-white w-6" /> : <FilterIcon className="fill-white w-6" />}
                    <span className="text-white">Filter</span>
                </button>
            </div>

            <div className={`flex flex-col xl:flex-row gap-5 justify-between mt-2 ${isFilterOpen ? 'flex' : 'hidden xl:flex'}`}>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                    <FilterCard title='Nama Pelanggan' width='w-full sm:w-56'>
                        <input className="input" value={customerName} onChange={(e) => setCustomerName(e.target.value.toUpperCase())} onKeyDown={filterEnterKeyPress} />
                    </FilterCard>
                    <FilterCard title='Phone' width='w-full sm:w-56'>
                        <input className="input" value={phone} onChange={(e) => setPhone(removeNonNumeric(e.target.value))} onKeyDown={filterEnterKeyPress} />
                    </FilterCard>
                    <FilterCard title='Status' width='w-full sm:w-32'>
                        <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => setStatus(e.value)} placeholder='All' />
                    </FilterCard>
                </div>
            </div>

            <div className="mt-3 flex w-full justify-end">
                <AddButton onClick={addBtnClick} title='Tambah Pelanggan' />
            </div>
           
            <div className="hidden xl:block mt-3">
                <DataTable
                    columns={columns}
                    data={customerList?.data?.listData ?? []}
                />
            </div>

            <div className="block xl:hidden mt-5">
                <div className="flex flex-col gap-3">
                    {customerList?.data?.listData?.map((data, i) => {
                        return(
                            <div className="flex justify-between border bg-white p-3" key={i}>
                                <div className="flex flex-col">
                                    <label className="text-sm">{data.customerName.toUpperCase()}</label>
                                    <label className="text-sm">{data.address === EmptyString ? "- " : data.address}</label>
                                    <label className="text-sm">{data.phone}</label>
                                </div>
                                <div className="flex flex-col gap-2 justify-between">
                                    <button className="button flex gap-2" onClick={() => editBtnClick(data.customerId)}>
                                        <EditIcon className="w-4 fill-white" />
                                    </button>
                                    <button className={`button flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.customerId, data.status)}>
                                        <TrashIcon className="w-4 fill-white" />
                                    </button>
                                    <button className={`button flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.customerId, data.status)}>
                                        <RevisionIcon className="w-4 fill-white" />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}